import { useCallback, useEffect, useRef, useState } from 'react';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router';
import { GridFilterModel } from '@rossum/ui/x-data-grid-pro';
import { parse, stringify } from '../../../lib/url';
import {
  DOCUMENTS_QUERY,
  DOCUMENTS_QUERY_FOR_BROWSER_TAB,
  EMAILS_QUERY,
} from '../../../redux/modules/localStorage/actions';
import { useJsonParse } from '../../../utils/hooks/useJsonParse';
import {
  getStringFromValue,
  useStoredState,
} from '../../../utils/hooks/useStoredState';
import { DEFAULT_PAGE_SIZE } from '../../document-list-base/hooks/usePagination';
import { LevelOptions, ViewOptions } from '../../document-list-base/types';
import { createNewFilterQuery } from '../../mql/transformers';
import { SAVED_VIEW_QUERY_NAME } from '../../saved-views/core/constants';

const queryKeys = [
  'search' as const,
  'ordering' as const,
  'filtering' as const,
  'page' as const,
  'level' as const,
  'view' as const,
  'page_size' as const,
  'selected_annotation' as const,
  'selected_annotation_view' as const,
  'selected_relation' as const,
  SAVED_VIEW_QUERY_NAME,
];

export const getQueueId = (
  existingFilter: GridFilterModel | null,
  level: LevelOptions | null
) => {
  const queueId: string | undefined = existingFilter?.items.find(
    item => item.field === 'queue' && item.value.length === 1
  )?.value[0];

  return queueId && level === 'queue' ? parseInt(queueId, 10) : undefined;
};

export const validateLevel = (value: string | undefined): LevelOptions | null =>
  value === 'all' || value === 'queue' || value === 'initial' ? value : null;

const getQueryFromStorage = (view: ViewOptions) => {
  const storedQuery = localStorage.getItem(
    view === 'emails' ? EMAILS_QUERY : DOCUMENTS_QUERY
  );

  if (!storedQuery) return undefined;

  const parsedData = queryString.parse(storedQuery);

  const search = getStringFromValue(parsedData['search']) ?? '';
  const ordering = getStringFromValue(parsedData['ordering']) ?? '';
  const filtering = getStringFromValue(parsedData['filtering']) ?? '';
  const level = validateLevel(getStringFromValue(parsedData['level']));
  const page = getStringFromValue(parsedData['page']) ?? '1';

  return {
    ...(search ? { search } : {}),
    ...(filtering ? { filtering } : {}),
    ...(ordering ? { ordering } : {}),
    ...(level ? { level } : {}),
    view,
    page,
  };
};

const getFilterWithQueueId = (filter: GridFilterModel, queueId: number) =>
  ({
    ...filter,
    items: filter.items.map(item => {
      if (item.field === 'queue') {
        return {
          ...item,
          value: [`${queueId}`],
        };
      }

      return item;
    }),
  }).items;

const keysToBeOmitted = [
  'selected_annotation',
  'selected_annotation_view',
  'selected_relation',
];

// Documents URLs carry no `view` param until the first view switch, so a
// missing or invalid value means the documents view.
const getViewFromSearch = (search: string): ViewOptions => {
  const viewQuery = queryString.parse(search)['view'];

  return viewQuery === 'emails' || viewQuery === 'documents'
    ? viewQuery
    : 'documents';
};

export const useDashboardQuery = () => {
  const { push, replace } = useHistory();
  const { search } = useLocation();

  // One-shot switch consumed by the next URL write, so programmatic
  // redirects (e.g. leaving the emails view of a queue without an inbox)
  // can replace the current history entry instead of truncating the
  // forward history with a push.
  const replaceNextHistoryWriteRef = useRef(false);

  const historyFn = useCallback(
    (location: Parameters<typeof push>[0]) => {
      const writeFn = replaceNextHistoryWriteRef.current ? replace : push;
      replaceNextHistoryWriteRef.current = false;
      writeFn(location);
    },
    [push, replace]
  );

  // take emails from url, before query in context of this hook is created, for solving which view should be handled first
  const [view, setView] = useState<ViewOptions>(() =>
    getViewFromSearch(search)
  );

  // Keep view in sync with the URL so browser back/forward navigation
  // crossing the documents <-> emails boundary updates the content too.
  useEffect(() => {
    setView(getViewFromSearch(search));
  }, [search]);

  const storageKey = view === 'emails' ? EMAILS_QUERY : DOCUMENTS_QUERY;

  const [query, setQuery] = useStoredState({
    name: storageKey,
    defaultValues: {
      page: '1',
      page_size: `${DEFAULT_PAGE_SIZE}`,
      level: 'initial',
    },
    stringKeys: queryKeys,
    historyFn,
    parseFn: storedSearch => parse(storedSearch, { arrayFormat: 'none' }),
    omitFromLocalStorage: keysToBeOmitted,
  });

  const [existingFilter] = useJsonParse<GridFilterModel>(
    query['filtering'] ?? ''
  );

  const level = validateLevel(query['level']);

  const queueIdFromUrl = getQueueId(existingFilter, level);

  const [emailExistingFilter] = useJsonParse<GridFilterModel>(
    getQueryFromStorage('emails')?.filtering ?? ''
  );

  const [documentsExistingFilter] = useJsonParse<GridFilterModel>(
    getQueryFromStorage('documents')?.filtering ?? ''
  );

  const handleSetQuery = useCallback(
    (targetView: ViewOptions, options?: { replaceHistory?: boolean }) => {
      // this should never happen as this function only runs in queue level, never in all docs level
      if (!queueIdFromUrl) return;

      replaceNextHistoryWriteRef.current = options?.replaceHistory ?? false;

      const isTargetEmails = targetView === 'emails';

      const targetFilter = isTargetEmails
        ? emailExistingFilter
        : documentsExistingFilter;

      const targetFilterQuery = createNewFilterQuery(
        targetFilter
          ? getFilterWithQueueId(targetFilter, queueIdFromUrl)
          : [
              {
                field: 'queue',
                operator: 'isAnyOf',
                value: [`${queueIdFromUrl}`],
              },
            ]
      );

      const targetQuery = {
        ...(getQueryFromStorage(targetView) ?? { view: targetView }),
        filtering: targetFilterQuery,
      };

      setQuery(prevQuery => {
        // when searching does not exists in target query, we don't want preserve it from prevQuery
        const { search: _ignoredPreviousSearch, ...restPrevQuery } = prevQuery;

        return {
          ...restPrevQuery,
          ...targetQuery,
        };
      });
      setView(targetView);
    },
    [queueIdFromUrl, emailExistingFilter, documentsExistingFilter, setQuery]
  );

  const queryView: ViewOptions =
    query['view'] === 'emails' ? 'emails' : 'documents';

  // // `documents:query` is the dashboard's entry record — a bare `/documents` URL
  // // (what the Documents tab in the navbar links to) is restored from it — so it
  // // carries the view the dashboard was last on. Only that field is written here;
  // // the documents query stored alongside it stays intact, and the emails view
  // // keeps its own query in `emails:query`.
  useEffect(() => {
    const entryRecord = parse(localStorage.getItem(DOCUMENTS_QUERY) ?? '');

    if (getStringFromValue(entryRecord['view']) === queryView) return;

    localStorage.setItem(
      DOCUMENTS_QUERY,
      stringify({ ...entryRecord, view: queryView })
    );
  }, [queryView]);

  useEffect(() => {
    // needed because of getReviewableStatusesFromAnnotationsQuery
    sessionStorage.setItem(DOCUMENTS_QUERY_FOR_BROWSER_TAB, stringify(query));
  }, [query]);

  return {
    query,
    setQuery,
    view,
    setView: handleSetQuery,
    existingFilter,
    level,
    currentQueueId: queueIdFromUrl,
  };
};

export type DashboardQuery = ReturnType<typeof useDashboardQuery>['query'];
